import type { Currency } from '@ps/web-core-enums';
import { FeatureFlag, Size } from '@ps/web-core-enums';
import type { TableParams } from '@ps/web-shared-api-types/v0/SwHomeGames';
import { Input, useBodyScrollLock, usePrevious, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import CoachMark from '@swf/components/CoachMark/CoachMark';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import useFeature from '@swf/core/hooks/useFeature';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { RootState } from '@swf/core/store';
import {
  PKR_MAX_SYMBOLS_TOURN_NAME,
  PKR_MIN_SYMBOLS_TOURN_NAME,
  PKR_RESTRICTED_SYMBOLS,
  PKR_RESTRICTED_SYMBOLS_STRING,
  mustBeBiggerOrEqual,
  mustBeLowerOrEqual,
  mustNotContainSymbols,
  useHomeGamesWidget
} from '@swf/poker';

import { HGOnboarding } from '../../../types/common';
import VideoChat from '../../CoachMark/CreateGame/VideoChat/VideoChat';
import Buttons from '../../Modal/Buttons/Buttons';
import VideoChatToggleV0 from '../../Modal/VideoChatToggle/VideoChatToggle';
import VideoChatToggleV1 from '../../Modal/VideoChatToggle/v1/VideoChatToggle';
import useForceUpdate from '../../Modal/hooks/useForceUpdate';
import BuyIns from '../BuyIns/BuyIns';
import useGetCurrency from '../hooks/useGetCurrency';
import useGetGameOptions from '../hooks/useGetGameOptions';
import useGetGameType from '../hooks/useGetGameType';
import useGetPlayersPerTable from '../hooks/useGetPlayersPerTable';
import useGetStakes from '../hooks/useGetStakes';
import type { FormValues } from '../utils/common';
import { getMatchingGameObject } from '../utils/common';

import styles from './Container.scss';
import {
  TXTCLI_Currency,
  TXTCLI_Players_Per_Table,
  TXTCLI_Stakes,
  TXTMOB_Create,
  TXTSRV_Game,
  TXTSRV_Table_names_must_be_between_6__ELL,
  TXTWCL_Table_Name,
  TXTWCL_The_following_characters_are_i_ELL,
  TXTWCL_Type_of_Game
} from './CreateGameForm.messages.json';

interface Props {
  gameData: TableParams[];
  loading: boolean;
}

interface SearchParams {
  widgetId: string;
}

const CreateGameForm = ({ gameData, loading }: Props) => {
  const { widgetId } = useSearchParams<SearchParams>();
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget(widgetId);

  const videoChatToggleRedesignEnabled = useFeature(
    FeatureFlag.PKRHomeGamesVideoChatToggleRedesign
  );
  const VideoChatToggle = videoChatToggleRedesignEnabled ? VideoChatToggleV1 : VideoChatToggleV0;

  const createGameOBEnabled = useFeature(FeatureFlag.PKRHomeGamesOnboardingCreateGame);
  const onboardingCompleted = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(rootState, `onboarding[${HGOnboarding.CreateCashGame}]`)
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  useBodyScrollLock(true, wrapperRef.current);
  const { greaterThan } = useScreenSize();

  const showCoachMark = createGameOBEnabled && !onboardingCompleted && greaterThan(Size.Small);

  const { change, getState } = useForm<FormValues>();
  const { values, validating, valid } = getState();
  const { handType, potStruct, tableSize, currency, stake, videoChatEnabled } = values;

  const gameOptions = useGetGameOptions(gameData);
  const gameTypeOptions = useGetGameType(gameData, handType);
  const playersPerTableOptions = useGetPlayersPerTable(gameData, handType, potStruct);
  const currencyOptions = useGetCurrency(gameData, handType, potStruct, tableSize);
  const stakesOptions = useGetStakes(gameData, values);

  const [videoSupported, setVideoSupported] = useState(false);
  const { currency: prevCurrency } = usePrevious(values);

  const { forceFieldUpdate } = useForceUpdate({
    values,
    change
  });

  useEffect(() => {
    if (loading) return;
    if (!handType || !currency || !stake) return;

    const tableParams = getMatchingGameObject({ ...values }, gameData);

    if (!tableParams && !videoSupported) return;
    if (currency !== prevCurrency) return;

    if (videoSupported && !tableParams) {
      setVideoSupported(false);

      if (videoChatEnabled) {
        change('videoChatEnabled', false);
      }

      return;
    }

    if (!tableParams) return;

    const { videoChatSupported } = tableParams;

    if (videoSupported !== videoChatSupported) {
      setVideoSupported(!!videoChatSupported);
    }
  }, [
    change,
    gameData,
    loading,
    values,
    handType,
    currency,
    stake,
    videoChatEnabled,
    videoSupported,
    prevCurrency
  ]);

  useEffect(() => {
    forceFieldUpdate({ field: 'potStruct', options: gameTypeOptions });
  }, [forceFieldUpdate, gameTypeOptions]);

  useEffect(() => {
    forceFieldUpdate({ field: 'tableSize', options: playersPerTableOptions });
  }, [forceFieldUpdate, playersPerTableOptions]);

  useEffect(() => {
    forceFieldUpdate({ field: 'currency', options: currencyOptions });
  }, [forceFieldUpdate, currencyOptions]);

  useEffect(() => {
    forceFieldUpdate({ field: 'stake', options: stakesOptions });
  }, [forceFieldUpdate, stakesOptions]);

  return (
    <>
      <div ref={wrapperRef} className={styles.form}>
        <Input
          id="tableName"
          label={TXTWCL_Table_Name}
          className={styles.tableName}
          validators={[
            mustNotContainSymbols(PKR_RESTRICTED_SYMBOLS, {
              ...TXTWCL_The_following_characters_are_i_ELL,
              values: { 0: PKR_RESTRICTED_SYMBOLS_STRING }
            }),
            mustBeBiggerOrEqual(PKR_MIN_SYMBOLS_TOURN_NAME, {
              ...TXTSRV_Table_names_must_be_between_6__ELL
            }),
            mustBeLowerOrEqual(PKR_MAX_SYMBOLS_TOURN_NAME, {
              ...TXTSRV_Table_names_must_be_between_6__ELL
            })
          ]}
        />
        <div>
          <StarsDropDown
            id="handType"
            label={TXTSRV_Game}
            options={gameOptions}
            defaultOption={gameOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <div>
          <StarsDropDown
            id="potStruct"
            label={TXTWCL_Type_of_Game}
            options={gameTypeOptions}
            defaultOption={gameTypeOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        {showCoachMark ? (
          <CoachMark
            content={
              <VideoChat
                onDismiss={() => {
                  widgetDispatch.setLocalStorageItem({
                    onboarding: { [HGOnboarding.CreateCashGame]: true }
                  });
                }}
              />
            }
            refElementClassName={classNames(styles.videoChatToggle, {
              [styles.videoChatToggleV1]: videoChatToggleRedesignEnabled
            })}
            placement="left-start"
          >
            <VideoChatToggle
              checked={videoChatEnabled}
              supported={videoSupported}
              onChange={() => change('videoChatEnabled', !videoChatEnabled)}
            />
          </CoachMark>
        ) : (
          <VideoChatToggle
            className={classNames(styles.videoChatToggle, {
              [styles.videoChatToggleV1]: videoChatToggleRedesignEnabled
            })}
            imageContainerClassName={styles.videoChatImageContainer}
            checked={videoChatEnabled}
            supported={videoSupported}
            onChange={() => change('videoChatEnabled', !videoChatEnabled)}
          />
        )}
        <div>
          <StarsDropDown
            id="tableSize"
            label={TXTCLI_Players_Per_Table}
            options={playersPerTableOptions}
            defaultOption={playersPerTableOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <div>
          <StarsDropDown
            id="currency"
            label={TXTCLI_Currency}
            options={currencyOptions}
            defaultOption={currencyOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <div className={styles.buyIn}>
          <StarsDropDown
            id="stake"
            label={TXTCLI_Stakes}
            options={stakesOptions}
            defaultOption={stakesOptions[0]}
            disabled={loading}
            className={styles.dropdown}
            caption={<BuyIns buyIns={values.stake} currency={values.currency as Currency} />}
          />
        </div>
      </div>
      <Buttons
        className={styles.submitGroup}
        disabled={loading || !valid || validating}
        translation={TXTMOB_Create}
      />
    </>
  );
};

export default CreateGameForm;
